/**
 * Secure Messages V2 - API Service Layer
 *
 * All API calls for the SecureMessages module.
 * Uses performRequest/performRequestSecond/performRequestThird from common/apiHandler.
 */

import { performRequest, performRequestFourth, performRequestSecond, performRequestThird } from 'common/apiHandler';
import { default as apiEndPoints } from 'common/apiEndPoints';
import { getTenantDetails } from 'utils/utils';

// ─── Message List V2 (RDS-based with offset pagination) ──────────────────────

/**
 * Fetch paginated message list with filters using new RDS-based API
 * @param {Object} params
 * @returns {Promise}
 */
export const fetchMessageListV2 = ({
  case_id = '',
  author_id = '',
  date_from = '',
  date_to = '',
  keyword = '',
  message_status = '',
  participants = '',
  sentiment = '',
  sort_order = 'desc',
  sort_by = '',
  limit = 10,
  offset = 0,
  is_show_global = false,
} = {}) => {
  const [tenantId, tenantSk] = getTenantDetails();

  const payload = {
    tenant_id: tenantId,
    sort_order,
    limit,
    offset,
  };

  // Add optional filters
  if (case_id) payload.case_id = case_id;
  if (author_id) payload.author_id = author_id;
  if (date_from) payload.date_from = date_from;
  if (date_to) payload.date_to = date_to;
  if (keyword) payload.keyword = keyword;
  if (message_status) payload.message_status = message_status;
  if (participants) payload.participants = participants;
  if (sentiment) payload.sentiment = sentiment;
  if (sort_by) payload.sort_by = sort_by;
  if (is_show_global) payload.is_show_global = is_show_global;

  const url = `${apiEndPoints.listMessagesV2}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`;

  return performRequestFourth(apiEndPoints.methodType.POST, url, payload);
};

// ─── Message Thread Details (Split into 2 APIs) ──────────────────────────────

/**
 * Fetch message thread details (metadata only - NEW API)
 * @param {string} messageThreadId - UUID of the message thread
 * @returns {Promise}
 */
export const fetchThreadDetails = (messageThreadId, excludeFromLoading = false) => {
  const [tenantId] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    message_thread_id: messageThreadId,
  });

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.getThreadDetails}?${queryParams.toString()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

/**
 * Fetch message thread replies (paginated with offset - NEW API)
 * @param {Object} params
 * @param {string} params.messageThreadId - UUID of the message thread
 * @param {number} params.limit - Number of replies to return (max: 100, default: 50)
 * @param {number} params.offset - Pagination offset (default: 0)
 * @returns {Promise}
 */
export const fetchThreadReplies = ({ messageThreadId, limit = 50, offset = 0, excludeFromLoading = false } = {}) => {
  const [tenantId] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    message_thread_id: messageThreadId,
    limit: String(limit),
    offset: String(offset),
  });

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.getThreadReplies}?${queryParams.toString()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

/**
 * Fetch message thread details (metadata + paginated replies) - LEGACY
 * @deprecated Use fetchThreadDetails + fetchThreadReplies instead
 * @param {Object} params
 * @returns {Promise}
 */
export const fetchMessageDetails = ({
  messageId,
  pageLimit = 100,
  pagination_token = '',
  pagination_direction = 'reverse',
  excludeFromLoading = false,
} = {}) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const pk = `${tenantId}#${messageId}`;

  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    sk: '',
    pk,
    limit: pageLimit,
    pagination_token,
    pagination_direction,
  });

  return performRequest(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.viewMessageThreadDetails}?${queryParams.toString()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

// ─── Create Message (NEW - via API) ──────────────────────────────────────────

/**
 * Create a new message thread via API
 * @param {Object} payload - Message payload following the new API schema
 * @returns {Promise}
 */
export const createMessage = (payload) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
  });

  if (payload.is_lead) queryParams.set('is_lead', payload.is_lead);

  return performRequestFourth(apiEndPoints.methodType.POST, `${apiEndPoints.createCaseMessageV2}?${queryParams.toString()}`, payload);
};

/**
 * Send a reply to a message thread via API
 * @param {Object} payload - Reply payload following the new API schema
 * @returns {Promise}
 */
export const sendReply = (payload) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    message_thread_id: payload.message_thread_id,
  });

  if (payload.is_lead) queryParams.set('is_lead', payload.is_lead);

  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.createCaseMessageV2}?${queryParams.toString()}`,
    payload,
    null,
    true,
    true
  );
};

// ─── Update Message Thread ───────────────────────────────────────────────────

/**
 * Update message thread (read/unread, resolution, assignment etc.)
 * @param {Object} data
 * @param {boolean} isLead
 * @returns {Promise}
 */
export const updateMessageThread = (data, isLead = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
  });

  if (isLead) queryParams.set('is_lead', isLead);

  return performRequest(
    apiEndPoints.methodType.PUT,
    `${apiEndPoints.updateMessageThread}?${queryParams.toString()}`,
    data,
    null,
    true,
    true
  );
};

// ─── Update Read Pointer (Unified) ───────────────────────────────────────────

/**
 * Update read status for message replies or sidebar messages
 * @param {Object} payload
 * @param {string} payload.message_thread_id - Required - UUID of the message thread
 * @param {string} payload.user_id - Required - firm_user_id or contact_id
 * @param {string} payload.message_type - Required - "reply" or "sidebar"
 * @param {string} payload.last_read_message_id - Optional - ID of last message read
 * @param {boolean} payload.unread - Optional - force unread status (default: false)
 * @returns {Promise}
 */
export const updateReadPointer = (payload) => {
  const { excludeFromLoading = false } = payload;
  delete payload.excludeFromLoading;
  const [tenantId] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
  });

  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.updateReadPointer}?${queryParams.toString()}`,
    payload,
    null,
    true,
    excludeFromLoading
  );
};

// ─── Invite Users ────────────────────────────────────────────────────────────

/**
 * Invite users/groups to a message thread
 * @param {string} sk - Message thread SK
 * @param {boolean} isLead
 * @param {Object} data - { invited_user: [], invited_group: [] }
 * @returns {Promise}
 */
export const inviteUsersToThread = (sk, isLead = false, data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
  });

  if (sk) queryParams.set('sk', sk);
  if (isLead) queryParams.set('is_lead', isLead);

  return performRequestThird(apiEndPoints.methodType.PATCH, `${apiEndPoints.sendInviteToUser}?${queryParams.toString()}`, data);
};

// ─── Change Message Case ─────────────────────────────────────────────────────

/**
 * Move message to a different case
 * @param {string} pk
 * @param {Object} data
 * @returns {Promise}
 */
export const changeMessageCase = (pk, data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
  });

  if (pk) queryParams.set('pk', pk);

  return performRequestThird(apiEndPoints.methodType.PATCH, `${apiEndPoints.chageMessageCase}?${queryParams.toString()}`, data);
};

// ─── Cancel Scheduled Message ────────────────────────────────────────────────

/**
 * Cancel a scheduled message
 * @param {string} messagePk
 * @param {string} messageSk
 * @returns {Promise}
 */
export const cancelScheduledMessage = (messagePk, messageSk) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    message_pk: messagePk,
    message_sk: messageSk,
  });

  return performRequestSecond(apiEndPoints.methodType.PUT, `${apiEndPoints.cancelScheduledMessage}?${queryParams.toString()}`, {
    action: 'cancel',
  });
};

// ─── Sidebar Messages ────────────────────────────────────────────────────────

/**
 * List sidebar messages with offset-based pagination (NEW API)
 * @param {Object} params
 * @param {string} params.caseId - Optional - Filter by case ID
 * @param {number} params.limit - Max 500, default: 20
 * @param {number} params.offset - Pagination offset, default: 0
 * @returns {Promise}
 */
export const listSidebarMessages = ({ message_thread_id, limit = 20, offset = 0 } = {}) => {
  const [tenantId] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    message_thread_id,
    limit,
    offset,
  });

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.listSidebarMessages}?${queryParams.toString()}`,
    {},
    null,
    true,
    true
  );
};

/**
 * Send a sidebar message via API (new pattern)
 * @param {Object} payload - Sidebar message payload
 * @returns {Promise}
 */
export const sendSidebarMessage = (payload) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
  });

  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.createCaseMessageV2}?${queryParams.toString()}`,
    payload,
    null,
    true,
    true
  );
};

// ─── Auto Save ───────────────────────────────────────────────────────────────

/**
 * Get auto-saved draft data
 * @param {string} caseId
 * @returns {Promise}
 */
export const getAutoSaveDraft = (caseId = '', messageThreadId = '') => {
  const [tenantId, tenantSk] = getTenantDetails();
  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    case_id: caseId,
  });

  if (messageThreadId) queryParams.set('message_thread_id', messageThreadId);

  return performRequestThird(apiEndPoints.methodType.GET, `${apiEndPoints.autoSave}?${queryParams.toString()}`);
};

// ─── Contacts & Users ────────────────────────────────────────────────────────

/**
 * Fetch client/user list for a case (recipients, authors)
 * @param {Object} params
 * @returns {Promise}
 */
export { getClientUserList } from 'services/users/userServices';
export { listUsers } from 'services/users/userServices';
export { totalCountEvent } from 'services/generalServices';
export { markNotification } from 'services/notification/notificationService';
export { listGoogleCalendars } from 'services/Calender/calendarServices';
export { validateUpload } from 'services/fileManager/fileManagerServices';
export { getSignedUrl } from 'modules/file_manager/services';
